import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  File,
  Folder,
  FolderOpen,
  Play,
  Pause,
  RotateCcw,
  Terminal as TerminalIcon,
  Clock,
  FileText,
  Database,
  ChevronRight,
  ChevronDown,
  Copy,
  Check,
  Plus,
  Trash2,
  MoreVertical,
  FilePlus,
  FolderPlus,
  X,
  Lightbulb,
  Package,
  CheckCircle,
  PlayCircle,
  Download,
  Maximize2,
  Minimize2,
  PanelLeft,
  Zap,
  Pencil,
  Loader2
} from 'lucide-react';
import { getFileIcon } from '../../utils/fileIcons';
import {
  FileTemplate,
  APIRequestTemplate,
  MachineCodingTemplate,
} from '../../data/machine-coding-templates/types';
import { templateSource, TemplateNotFoundError } from '../../data/machine-coding-templates/templateLoader';
import { extractFileContents } from '../../utils/machineCoding/fileTree';
import { resolveWorkingContents } from '../../utils/machineCoding/resume';
import { resetToTemplate } from '../../utils/machineCoding/reset';
import { downloadProjectZip } from '../../utils/downloadProjectZip';
import MonacoCodeEditor from '../../components/common/MonacoCodeEditor';
import Terminal from '../../components/common/Terminal';
import ApiTester from '../../components/common/ApiTester';
import WorkspaceOnboarding from '../../components/practice/WorkspaceOnboarding';
import PackageManager from '../../components/practice/PackageManager';
import { getWebContainer, buildFileSystemTree, onServerReady } from '../../services/webcontainer';
import { machineCodingProjectService, MachineCodingProject } from '../../services/machineCodingProjectService';

interface WorkspaceState {
  activePanel: 'files' | 'api' | 'packages' | 'tests' | 'timer';
  fileTree: FileTemplate[];
  fileContents: { [path: string]: string };
  originalFileContents: { [path: string]: string }; // Track original state
  modifiedFiles: Set<string>; // Track which files are modified
  activeFile: FileTemplate | null;
  apiRequests: APIRequestTemplate[];
  activeApiRequest: APIRequestTemplate | null;
  apiResponse: any | null;
  timerSeconds: number;
  timerRunning: boolean;
  timerSessions: TimerSession[];
}

interface TimerSession {
  id: string;
  startTime: Date;
  endTime?: Date;
  duration: number;
  description: string;
}

// Reflect the saved project name on the file-tree root folder (display only —
// paths are keyed off `path`, not `name`, so this is purely cosmetic).
const applyRootName = (tree: FileTemplate[], name: string | null): FileTemplate[] => {
  if (!name || tree.length === 0) return tree;
  return [{ ...tree[0], name }, ...tree.slice(1)];
};

const MachineCodingWorkspace: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  // When present, the workspace is editing a saved DB project (MCP@...) rather
  // than a fresh template. File edits autosave back to this project.
  const dbProjectId = searchParams.get('projectId');
  // Template bodies live outside the initial bundle and are loaded on demand
  // via the TemplateSource seam. While the dynamic import is pending we show a
  // loading state; an unknown id (or a failed load) renders the not-found state.
  const [template, setTemplate] = useState<MachineCodingTemplate | null>(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateNotFound, setTemplateNotFound] = useState(false);
  // Display name of the saved project (e.g. a custom workspace title). Falls
  // back to the template name when there's no saved project.
  const [projectName, setProjectName] = useState<string | null>(null);
  // Holds the saved project fetched during resolution so the init effect can
  // reuse it without a second network round-trip.
  const loadedProjectRef = useRef<MachineCodingProject | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTemplateLoading(true);
    setTemplateNotFound(false);
    setTemplate(null);
    setProjectName(null);
    setLoadError(null);
    loadedProjectRef.current = null;
    // Reset autosave guards: never autosave until we've hydrated from a
    // successful load for THIS session.
    hydratedRef.current = false;
    skipFirstAutosaveRef.current = true;
    skipFirstApiSaveRef.current = true;

    const resolve = async () => {
      try {
        // For a saved session the URL segment is a cosmetic slug; resolve the
        // real template from the project's `forkedFrom`. For an unsaved session
        // the URL segment IS the real template id.
        let realTemplateId = templateId || '';
        if (dbProjectId) {
          let project: MachineCodingProject;
          try {
            project = await machineCodingProjectService.getById(dbProjectId);
          } catch (e) {
            // Saved project failed to load — never overwrite it. Show an error
            // and keep autosave suspended (hydratedRef stays false).
            if (cancelled) return;
            console.error('Failed to load saved project:', e);
            setLoadError(
              'Could not load your saved project. Your saved work is NOT being overwritten — reload to try again.',
            );
            setTemplateLoading(false);
            setTemplateNotFound(true);
            return;
          }
          loadedProjectRef.current = project;
          if (project?.name) setProjectName(project.name);
          if (project?.forkedFrom) realTemplateId = project.forkedFrom;
        }

        if (!realTemplateId) throw new TemplateNotFoundError(templateId || '');

        const tpl = await templateSource.loadTemplate(realTemplateId);
        if (cancelled) return;
        setTemplate(tpl);
        setTemplateLoading(false);
      } catch (e) {
        if (cancelled) return;
        if (!(e instanceof TemplateNotFoundError)) {
          console.error('Failed to load template:', e);
        }
        // Unknown id / failed dynamic import → not-found state with a back link.
        setTemplateLoading(false);
        setTemplateNotFound(true);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [templateId, dbProjectId]);

  const [workspace, setWorkspace] = useState<WorkspaceState>({
    activePanel: 'files',
    fileTree: [],
    fileContents: {},
    originalFileContents: {},
    modifiedFiles: new Set(),
    activeFile: null,
    apiRequests: [],
    activeApiRequest: null,
    apiResponse: null,
    timerSeconds: 0,
    timerRunning: false,
    timerSessions: []
  });

  const [showTerminal, setShowTerminal] = useState(true);
  // Active tab in the bottom panel (VS Code-style dock).
  const [bottomTab, setBottomTab] = useState<'terminal' | 'output' | 'api' | 'packages'>('terminal');
  const [isDockResizing, setIsDockResizing] = useState(false);
  // Editor maximize (hide explorer + bottom dock) and explorer collapse, VS Code-style.
  const [editorMaximized, setEditorMaximized] = useState(false);
  const [showExplorer, setShowExplorer] = useState(true);
  const [explorerWidth, setExplorerWidth] = useState(() => {
    const savedWidth = Number(localStorage.getItem('machine-coding-explorer-width'));
    return Number.isFinite(savedWidth) && savedWidth >= 220 && savedWidth <= 480 ? savedWidth : 288;
  });
  const [isExplorerResizing, setIsExplorerResizing] = useState(false);
  // True fullscreen: entire workspace takes over viewport (no nav bar).
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Bottom panel maximized (terminal/output takes full height).
  const [bottomPanelMaximized, setBottomPanelMaximized] = useState(false);
  // Autosave status indicator.
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // Data-safety guards: never autosave until the saved project has loaded OK,
  // so a failed/partial load can't overwrite the good copy in the DB.
  const hydratedRef = useRef(false);
  const skipFirstAutosaveRef = useRef(true);
  // Separate guard so the API-request autosave doesn't fire on hydration.
  const skipFirstApiSaveRef = useRef(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Manual "Add API request" form state for the API tab.
  const [showAddReq, setShowAddReq] = useState(false);
  const [newReq, setNewReq] = useState<{ name: string; method: string; url: string }>({
    name: '',
    method: 'GET',
    url: '',
  });
  // Inline rename for a saved request.
  const [editingReqId, setEditingReqId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [terminalHeight, setTerminalHeight] = useState(192);
  const [isRunning, setIsRunning] = useState(false);
  // Dedicated state for the editor "Run" action (separate from the server flow,
  // which uses isRunning). A run can be a long-lived process (e.g. a server that
  // listens forever), so we keep a handle to kill it and toggle Run ⇄ Stop.
  const [isExecuting, setIsExecuting] = useState(false);
  const executeProcRef = useRef<any>(null);

  // Strip ANSI escape codes (colors, cursor moves, spinners) for the plain-text
  // Output popup, which is a <pre> and not a real terminal emulator.
  // eslint-disable-next-line no-control-regex
  const stripAnsi = (s: string) => s.replace(/\x1b\[[0-9;]*[A-Za-z]/g, '');
  const [executionOutput, setExecutionOutput] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [testResults, setTestResults] = useState<{
    passed: boolean;
    output: string;
    error?: string;
  } | null>(null);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    folderPath: string;
    fileType?: 'file' | 'folder';
    filePath?: string;
  } | null>(null);
  // VS Code-style inline new file/folder creation in the explorer (no prompts).
  const [creatingNode, setCreatingNode] = useState<{ parentPath: string; type: 'file' | 'folder' } | null>(null);
  const [creatingName, setCreatingName] = useState('');
  // Currently selected tree node (drives where new files/folders are created).
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<'file' | 'folder' | null>(null);
  // Inline rename state.
  const [renamingPath, setRenamingPath] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [serverUrl, setServerUrl] = useState<string | null>(null);
  const [serverPort, setServerPort] = useState<number | null>(null);
  const [serverId, setServerId] = useState<string | null>(null);
  const [isServerRunning, setIsServerRunning] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(() => {
    // Check if user has seen onboarding for this template
    const key = `onboarding_seen_${templateId}`;
    return localStorage.getItem(key) === 'true';
  });
  // Removed unused state: serverProcess, setServerProcess

  // When code inside WebContainer starts listening on a port (e.g. app.listen),
  // WebContainer emits `server-ready` with a real, browser-reachable preview URL
  // for that in-container server. We capture it so the preview link and API
  // tester can target it. This fully replaces the old host-based "Start Server"
  // flow — code now runs only in the sandboxed browser container, never on our host.
  useEffect(() => {
    const unsubscribe = onServerReady((port, url) => {
      setServerUrl(url);
      setServerPort(port);
      setIsServerRunning(true);
    });
    return unsubscribe;
  }, []);

  // Ctrl+Enter / Cmd+Enter keyboard shortcut to Run/Stop
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        executeCode();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Show onboarding for new users
  useEffect(() => {
    if (!template) return;
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }
  }, [template, hasSeenOnboarding]);

  // Initialize workspace and WebContainer
  useEffect(() => {
    if (!template) return;

    const initializeWorkspace = async () => {
      // Derive the template's default File_Contents from its Source_Tree.
      const templateDefaults = extractFileContents(template.fileStructure);

      // The saved project (if any) was already fetched during resolution; the
      // template only renders when that fetch succeeded, so reading it from the
      // ref here is safe and avoids a second network round-trip.
      const project = loadedProjectRef.current;
      const savedName: string | null = project?.name ?? null;
      const savedContents: { [path: string]: string } | null =
        project?.fileContents && Object.keys(project.fileContents).length > 0
          ? project.fileContents
          : null;
      // Saved API-tester requests (null for legacy projects → use template defaults).
      const savedApiRequests: any[] | null = Array.isArray(project?.apiRequests)
        ? (project!.apiRequests as any[])
        : null;

      const initialContents: { [path: string]: string } = resolveWorkingContents(
        templateDefaults,
        savedContents,
      );

      // Initialize WebContainer and mount file system
      try {
        console.log('🔄 Initializing WebContainer...');
        const wc = await getWebContainer();
        console.log('✅ WebContainer instance obtained');

        // Wait a moment for WebContainer to be fully ready
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check if already mounted by trying to read a file
        let isAlreadyMounted = false;
        try {
          await wc.fs.readFile('package.json', 'utf-8');
          isAlreadyMounted = true;
          console.log('📦 WebContainer already mounted, skipping remount');
        } catch {
          // Not mounted yet, proceed with mounting
          isAlreadyMounted = false;
        }

        if (!isAlreadyMounted) {
          // Ensure a package.json exists so `npm install` / `tsx` have a project.
          if (!initialContents['/package.json'] && !initialContents['package.json']) {
            initialContents['/package.json'] = JSON.stringify(
              {
                name: template.name.toLowerCase().replace(/\s+/g, '-'),
                version: '1.0.0',
                type: 'module',
                scripts: {
                  dev: 'tsx src/app.ts',
                  build: 'tsc',
                  start: 'npm run build && node dist/app.js',
                },
                dependencies: {},
                devDependencies: {
                  '@types/node': '^20.0.0',
                  tsx: '^4.0.0',
                  typescript: '^5.0.0',
                },
              },
              null,
              2,
            );
          }

          // Build a proper nested FileSystemTree (flat slash-paths won't nest).
          const tree = buildFileSystemTree(initialContents);

          await wc.mount(tree);
          console.log('✅ WebContainer mounted successfully');

          // Wait a moment for mount to fully complete
          await new Promise(resolve => setTimeout(resolve, 500));
          console.log('✅ WebContainer ready for commands');
        }
      } catch (error) {
        console.error('❌ Failed to mount WebContainer:', error);
      }

      setWorkspace(prev => ({
        ...prev,
        fileTree: applyRootName(template.fileStructure, savedName),
        fileContents: initialContents,
        originalFileContents: { ...initialContents }, // Store original state
        modifiedFiles: new Set(),
        apiRequests: savedApiRequests ?? (template.apiRequests || []),
        activeFile: template.fileStructure[0]?.children?.[0]?.children?.[0] || null
      }));

      // Only allow autosave/flush once we've hydrated. The template only renders
      // after a successful project fetch (failures show an error and never reach
      // here), so enabling autosave now cannot overwrite a project we failed to read.
      hydratedRef.current = true;
    };

    initializeWorkspace();
  }, [template]);

  // Debounced autosave of file edits to the DB project (only for saved projects).
  // Code still executes in the browser; this just persists the working tree so it
  // survives reloads and follows the user across devices.
  useEffect(() => {
    if (!dbProjectId) return;
    if (Object.keys(workspace.fileContents).length === 0) return;
    // Never autosave until the saved project has hydrated from a successful load.
    if (!hydratedRef.current) return;
    // Skip the one render caused by hydration itself.
    if (skipFirstAutosaveRef.current) {
      skipFirstAutosaveRef.current = false;
      return;
    }

    setSaveStatus('saving');
    const handle = setTimeout(() => {
      machineCodingProjectService
        .update(dbProjectId, { fileContents: workspace.fileContents })
        .then(() => setSaveStatus('saved'))
        .catch((e) => { console.error('Autosave failed:', e); setSaveStatus('error'); });
    }, 1500);

    return () => clearTimeout(handle);
  }, [dbProjectId, workspace.fileContents]);

  // Debounced autosave of the API-tester request collection (add/edit/remove/
  // rename), so a user's requests survive reloads alongside their code.
  useEffect(() => {
    if (!dbProjectId) return;
    if (!hydratedRef.current) return;
    // Skip the render caused by hydration itself.
    if (skipFirstApiSaveRef.current) {
      skipFirstApiSaveRef.current = false;
      return;
    }

    setSaveStatus('saving');
    const handle = setTimeout(() => {
      machineCodingProjectService
        .update(dbProjectId, { apiRequests: workspace.apiRequests })
        .then(() => setSaveStatus('saved'))
        .catch((e) => { console.error('API autosave failed:', e); setSaveStatus('error'); });
    }, 1500);

    return () => clearTimeout(handle);
  }, [dbProjectId, workspace.apiRequests]);

  // Best-effort flush of the latest file tree when the tab is hidden/closed or
  // the user navigates away, so crash/interrupt recovery is as fresh as possible
  // (the debounced autosave above already bounds loss to ~1.5s).
  const latestContentsRef = useRef(workspace.fileContents);
  const latestApiRef = useRef(workspace.apiRequests);
  useEffect(() => {
    latestContentsRef.current = workspace.fileContents;
  }, [workspace.fileContents]);
  useEffect(() => {
    latestApiRef.current = workspace.apiRequests;
  }, [workspace.apiRequests]);

  useEffect(() => {
    if (!dbProjectId) return;
    const flush = () => {
      if (!hydratedRef.current) return; // don't overwrite the DB with un-hydrated state
      machineCodingProjectService
        .update(dbProjectId, { fileContents: latestContentsRef.current, apiRequests: latestApiRef.current })
        .catch(() => { /* best effort */ });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVisibility);
      flush(); // also flush on unmount (route change)
    };
  }, [dbProjectId]);

  // Resize the bottom dock by dragging its handle (height clamped so the editor
  // and the dock's own tab strip always stay visible).
  useEffect(() => {
    if (!isDockResizing) return;
    const onMove = (e: MouseEvent) => {
      const next = window.innerHeight - e.clientY;
      setTerminalHeight(Math.max(140, Math.min(window.innerHeight - 220, next)));
    };
    const onUp = () => setIsDockResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDockResizing]);

  // Resize the file explorer horizontally. Keep enough room for both the tree
  // labels and the editor, and remember the user's preferred width.
  useEffect(() => {
    if (!isExplorerResizing) return;
    const onMove = (e: MouseEvent) => {
      const nextWidth = Math.max(220, Math.min(480, e.clientX));
      setExplorerWidth(nextWidth);
    };
    const onUp = () => {
      setIsExplorerResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isExplorerResizing]);

  useEffect(() => {
    localStorage.setItem('machine-coding-explorer-width', String(explorerWidth));
  }, [explorerWidth]);

  // Timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (workspace.timerRunning) {
      interval = setInterval(() => {
        setWorkspace(prev => ({ ...prev, timerSeconds: prev.timerSeconds + 1 }));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [workspace.timerRunning]);

  const toggleFileFolder = (fileId: string) => {
    const updateFileTree = (files: FileTemplate[]): FileTemplate[] => {
      return files.map(file => {
        if (file.id === fileId && file.type === 'folder') {
          return { ...file, isOpen: !file.isOpen };
        }
        if (file.children) {
          return { ...file, children: updateFileTree(file.children) };
        }
        return file;
      });
    };

    setWorkspace(prev => ({
      ...prev,
      fileTree: updateFileTree(prev.fileTree)
    }));
  };

  const selectFile = (file: FileTemplate) => {
    if (file.type === 'file') {
      setWorkspace(prev => ({ ...prev, activeFile: file }));
    }
  };

  // Listen for Monaco "Go to Definition" cross-file navigation
  useEffect(() => {
    const handleMonacoNavigate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.path) return;
      const targetPath = detail.path.startsWith('/') ? detail.path : `/${detail.path}`;
      // Find the file in the tree by path
      const findFile = (nodes: FileTemplate[]): FileTemplate | null => {
        for (const node of nodes) {
          if (node.path === targetPath) return node;
          if (node.children) {
            const found = findFile(node.children);
            if (found) return found;
          }
        }
        return null;
      };
      const file = findFile(workspace.fileTree);
      if (file && file.type === 'file') {
        selectFile(file);
      }
    };
    window.addEventListener('monaco-navigate-to-file', handleMonacoNavigate);
    return () => window.removeEventListener('monaco-navigate-to-file', handleMonacoNavigate);
  }, [workspace.fileTree]);

  const updateFileContent = async (path: string, content: string) => {
    // Update WebContainer file system
    try {
      const wc = await getWebContainer();
      const cleanPath = path.startsWith('/') ? path.slice(1) : path;
      await wc.fs.writeFile(cleanPath, content);
    } catch (error) {
      console.error('Failed to write file to WebContainer:', error);
    }

    // Update workspace state
    setWorkspace(prev => {
      const isModified = prev.originalFileContents[path] !== content;
      const newModifiedFiles = new Set(prev.modifiedFiles);

      if (isModified) {
        newModifiedFiles.add(path);
      } else {
        newModifiedFiles.delete(path);
      }

      return {
        ...prev,
        fileContents: { ...prev.fileContents, [path]: content },
        modifiedFiles: newModifiedFiles
      };
    });
  };

  const saveFile = (path: string) => {
    setWorkspace(prev => {
      const newModifiedFiles = new Set(prev.modifiedFiles);
      newModifiedFiles.delete(path);

      return {
        ...prev,
        originalFileContents: {
          ...prev.originalFileContents,
          [path]: prev.fileContents[path]
        },
        modifiedFiles: newModifiedFiles
      };
    });

    // Show save confirmation
    setCopiedCode('saved');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const saveAllFiles = () => {
    setWorkspace(prev => ({
      ...prev,
      originalFileContents: { ...prev.fileContents },
      modifiedFiles: new Set()
    }));

    setCopiedCode('all-saved');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const resetFile = async (path: string) => {
    const originalContent = workspace.originalFileContents[path] || '';

    // Update WebContainer file system
    try {
      const wc = await getWebContainer();
      const cleanPath = path.startsWith('/') ? path.slice(1) : path;
      await wc.fs.writeFile(cleanPath, originalContent);
    } catch (error) {
      console.error('Failed to reset file in WebContainer:', error);
    }

    setWorkspace(prev => {
      const newModifiedFiles = new Set(prev.modifiedFiles);
      newModifiedFiles.delete(path);

      return {
        ...prev,
        fileContents: {
          ...prev.fileContents,
          [path]: originalContent
        },
        modifiedFiles: newModifiedFiles
      };
    });
  };

  const resetRepository = async () => {
    // Confirm before resetting entire repository
    const confirmed = window.confirm(
      `Are you sure you want to reset the entire repository?\n\nThis will:\n• Reset ALL modified files to their original state\n• Remove any npm installed packages\n• Cannot be undone\n\nModified files: ${workspace.modifiedFiles.size}`
    );

    if (confirmed) {
      // Restore the TEMPLATE defaults (re-derived from the freshly loaded
      // template), NOT originalFileContents — which for a Saved_Session equals
      // the loaded snapshot and would not undo edits back to the template.
      const resetContents: { [path: string]: string } = template
        ? { ...resetToTemplate(template) }
        : { ...workspace.originalFileContents };
      if (
        template &&
        !resetContents['/package.json'] &&
        !resetContents['package.json']
      ) {
        resetContents['/package.json'] = JSON.stringify(
          {
            name: template.name.toLowerCase().replace(/\s+/g, '-'),
            version: '1.0.0',
            type: 'module',
            scripts: {
              dev: 'tsx src/app.ts',
              build: 'tsc',
              start: 'npm run build && node dist/app.js',
            },
            dependencies: {},
            devDependencies: {
              '@types/node': '^20.0.0',
              tsx: '^4.0.0',
              typescript: '^5.0.0',
            },
          },
          null,
          2,
        );
      }

      try {
        const wc = await getWebContainer();
        await wc.mount(buildFileSystemTree(resetContents));
        console.log('✅ WebContainer reset successfully');
      } catch (error) {
        console.error('Failed to reset WebContainer:', error);
      }

      // Reset the entire repository to the template's default state, restoring
      // the original Source_Tree (so files deleted during the session reappear)
      // and clearing modified flags.
      setWorkspace(prev => ({
        ...prev,
        fileContents: resetContents,
        originalFileContents: { ...resetContents },
        modifiedFiles: new Set(),
        fileTree: template ? applyRootName(template.fileStructure, projectName) : prev.fileTree.filter(f =>
          f.name !== 'package-lock.json' && f.name !== 'node_modules'
        ),
      }));

      // Show confirmation
      setCopiedCode('repo-reset');
      setTimeout(() => setCopiedCode(null), 3000);
    }
  };

  // Terminal functionality is now handled by the independent Terminal component

  const startTimer = () => {
    setWorkspace(prev => ({ ...prev, timerRunning: true }));
  };

  const pauseTimer = () => {
    setWorkspace(prev => ({ ...prev, timerRunning: false }));
  };

  const resetTimer = () => {
    setWorkspace(prev => ({ ...prev, timerRunning: false, timerSeconds: 0 }));
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Open a folder (by path) so a freshly-started inline input is visible.
  const openFolderByPath = (tree: FileTemplate[], target: string): FileTemplate[] =>
    tree.map((n) => {
      if (n.type === 'folder' && n.path === target) return { ...n, isOpen: true };
      if (n.children) return { ...n, children: openFolderByPath(n.children, target) };
      return n;
    });

  const findByPath = (tree: FileTemplate[], path: string): FileTemplate | null => {
    for (const n of tree) {
      if (n.path === path) return n;
      if (n.children) {
        const found = findByPath(n.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  // Where a new file/folder should be created, VS Code-style: inside the
  // selected folder, inside the selected file's folder, else the workspace root.
  const getCreateParent = (): string => {
    if (selectedPath && selectedType === 'folder') return selectedPath;
    if (selectedPath && selectedType === 'file') {
      const idx = selectedPath.lastIndexOf('/');
      return idx > 0 ? selectedPath.slice(0, idx) : '/';
    }
    return workspace.fileTree[0]?.path || '/';
  };

  // Begin VS Code-style inline creation: render an input row under the parent.
  const beginCreate = (parentPath: string, type: 'file' | 'folder') => {
    setWorkspace((prev) => ({ ...prev, fileTree: openFolderByPath(prev.fileTree, parentPath) }));
    setCreatingNode({ parentPath, type });
    setCreatingName('');
    setContextMenu(null);
  };

  // Commit the inline input → create the file/folder (no browser prompt).
  const commitCreate = async () => {
    const node = creatingNode;
    const name = creatingName.trim();
    setCreatingNode(null);
    setCreatingName('');
    if (!node || !name) return;
    // Silently ignore invalid names (no popup) — VS Code-like behavior.
    if (!name.match(/^[a-zA-Z0-9._-]+$/)) return;
    if (node.type === 'file') await createFileAt(node.parentPath, name);
    else await createFolderAt(node.parentPath, name);
  };

  // Begin inline rename of a node.
  const beginRename = (node: FileTemplate) => {
    setRenamingPath(node.path);
    setRenameValue(node.name);
    setContextMenu(null);
  };

  // Rewrite a path→contents map when a file/folder at oldPath becomes newPath
  // (handles a renamed file and every descendant of a renamed folder).
  const rewriteContentKeys = (
    contents: { [path: string]: string },
    oldPath: string,
    newPath: string,
  ): { [path: string]: string } => {
    const next: { [path: string]: string } = {};
    for (const [k, v] of Object.entries(contents)) {
      if (k === oldPath) next[newPath] = v;
      else if (k.startsWith(oldPath + '/')) next[newPath + k.slice(oldPath.length)] = v;
      else next[k] = v;
    }
    return next;
  };

  // Rename a node in the tree, rewriting descendant paths for a folder.
  const renameInTree = (
    tree: FileTemplate[],
    oldPath: string,
    newPath: string,
    newName: string,
  ): FileTemplate[] =>
    tree.map((n) => {
      if (n.path === oldPath) {
        const rewrite = (node: FileTemplate): FileTemplate => ({
          ...node,
          path:
            node.path === oldPath
              ? newPath
              : node.path.startsWith(oldPath + '/')
                ? newPath + node.path.slice(oldPath.length)
                : node.path,
          children: node.children ? node.children.map(rewrite) : node.children,
        });
        return { ...rewrite(n), name: newName };
      }
      if (n.children) return { ...n, children: renameInTree(n.children, oldPath, newPath, newName) };
      return n;
    });

  // Commit an inline rename: update the tree, contents, active file, and resync
  // the WebContainer file system.
  const commitRename = async () => {
    const oldPath = renamingPath;
    const name = renameValue.trim();
    setRenamingPath(null);
    setRenameValue('');
    if (!oldPath || !name) return;
    if (!name.match(/^[a-zA-Z0-9._-]+$/)) return;
    const idx = oldPath.lastIndexOf('/');
    const parent = idx > 0 ? oldPath.slice(0, idx) : '';
    const newPath = `${parent}/${name}`;
    if (newPath === oldPath) return;

    const newContents = rewriteContentKeys(workspace.fileContents, oldPath, newPath);
    const newOriginal = rewriteContentKeys(workspace.originalFileContents, oldPath, newPath);
    const newTree = renameInTree(workspace.fileTree, oldPath, newPath, name);

    setWorkspace((prev) => {
      let activeFile = prev.activeFile;
      if (activeFile) {
        if (activeFile.path === oldPath) activeFile = { ...activeFile, name, path: newPath };
        else if (activeFile.path.startsWith(oldPath + '/'))
          activeFile = { ...activeFile, path: newPath + activeFile.path.slice(oldPath.length) };
      }
      const newModified = new Set<string>();
      prev.modifiedFiles.forEach((m) => {
        if (m === oldPath) newModified.add(newPath);
        else if (m.startsWith(oldPath + '/')) newModified.add(newPath + m.slice(oldPath.length));
        else newModified.add(m);
      });
      return {
        ...prev,
        fileTree: newTree,
        fileContents: newContents,
        originalFileContents: newOriginal,
        modifiedFiles: newModified,
        activeFile,
      };
    });

    if (selectedPath === oldPath) setSelectedPath(newPath);

    // Resync the WebContainer FS to the renamed tree (mount overlays; it leaves
    // node_modules intact, same as the reset flow).
    try {
      const wc = await getWebContainer();
      await wc.mount(buildFileSystemTree(newContents));
    } catch (e) {
      console.error('Failed to resync WebContainer after rename:', e);
    }
  };

  // Create a file at the given parent path with the supplied name.
  const createFileAt = async (parentPath: string, fileName: string) => {
    const newPath = parentPath.endsWith('/') ? `${parentPath}${fileName}` : `${parentPath}/${fileName}`;
    const language = getFileLanguage(fileName);

    // Create empty file - no default content
    let defaultContent = '';

    // Only add minimal content for JSON files (needs valid JSON structure)
    if (language === 'json') {
      defaultContent = `{\n  \n}\n`;
    }
    // All other files start empty

    // Add to file tree recursively - find the folder and add file to it
    const addFileToTree = (tree: FileTemplate[]): FileTemplate[] => {
      return tree.map(item => {
        // Compare paths - handle both '/src' and 'src' formats
        const itemPathNormalized = item.path.replace(/\/$/, '') || '/';
        const targetPathNormalized = parentPath.replace(/\/$/, '') || '/';

        if (itemPathNormalized === targetPathNormalized && item.type === 'folder') {
          // Found the target folder, add file to its children
          const newFile: FileTemplate = {
            id: `file-${Date.now()}-${Math.random()}`,
            name: fileName,
            type: 'file',
            language,
            path: newPath,
            content: defaultContent
          };
          return {
            ...item,
            children: [...(item.children || []), newFile]
          };
        }
        if (item.type === 'folder' && item.children) {
          // Recursively search in children
          return {
            ...item,
            children: addFileToTree(item.children)
          };
        }
        return item;
      });
    };

    const updatedTree = addFileToTree(workspace.fileTree);

    const newFile: FileTemplate = {
      id: `file-${Date.now()}`,
      name: fileName,
      type: 'file',
      language,
      path: newPath,
      content: defaultContent
    };

    setWorkspace(prev => {
      const newFileContents = { ...prev.fileContents, [newPath]: defaultContent };
      const newOriginalContents = { ...prev.originalFileContents, [newPath]: defaultContent };

      return {
        ...prev,
        fileContents: newFileContents,
        originalFileContents: newOriginalContents,
        fileTree: updatedTree,
        activeFile: newFile
      };
    });

    // Write to WebContainer
    try {
      const wc = await getWebContainer();
      const cleanPath = newPath.startsWith('/') ? newPath.slice(1) : newPath;
      await wc.fs.writeFile(cleanPath, defaultContent);
    } catch (error) {
      console.error('Failed to create file in WebContainer:', error);
    }
  };

  // Create a folder at the given parent path with the supplied name.
  const createFolderAt = async (parentPath: string, folderName: string) => {
    const newPath = parentPath.endsWith('/') ? `${parentPath}${folderName}` : `${parentPath}/${folderName}`;

    // Add folder to tree recursively
    const addFolderToTree = (tree: FileTemplate[]): FileTemplate[] => {
      return tree.map(item => {
        // Compare paths - handle both '/src' and 'src' formats
        const itemPathNormalized = item.path.replace(/\/$/, '') || '/';
        const targetPathNormalized = parentPath.replace(/\/$/, '') || '/';

        if (itemPathNormalized === targetPathNormalized && item.type === 'folder') {
          // Found the target folder, add new folder to its children
          const newFolder: FileTemplate = {
            id: `folder-${Date.now()}-${Math.random()}`,
            name: folderName,
            type: 'folder',
            path: newPath,
            isOpen: false,
            children: []
          };
          return {
            ...item,
            children: [...(item.children || []), newFolder]
          };
        }
        if (item.type === 'folder' && item.children) {
          // Recursively search in children
          return {
            ...item,
            children: addFolderToTree(item.children)
          };
        }
        return item;
      });
    };

    const updatedTree = addFolderToTree(workspace.fileTree);

    setWorkspace(prev => ({
      ...prev,
      fileTree: updatedTree
    }));
  };

  // Delete file functionality
  const deleteFile = async (path: string) => {
    const fileName = path.split('/').pop() || path;
    if (!window.confirm(`Are you sure you want to delete "${fileName}"?\n\nThis action cannot be undone.`)) return;

    try {
      const wc = await getWebContainer();
      const cleanPath = path.startsWith('/') ? path.slice(1) : path;
      await wc.fs.rm(cleanPath);
    } catch (error) {
      console.error('Failed to delete file in WebContainer:', error);
    }

    // Remove from file tree
    const removeFromTree = (tree: FileTemplate[]): FileTemplate[] => {
      return tree
        .filter(item => item.path !== path)
        .map(item => {
          if (item.type === 'folder' && item.children) {
            return {
              ...item,
              children: removeFromTree(item.children)
            };
          }
          return item;
        });
    };

    setWorkspace(prev => {
      const newFileContents = { ...prev.fileContents };
      const newOriginalContents = { ...prev.originalFileContents };
      const newModifiedFiles = new Set(prev.modifiedFiles);

      delete newFileContents[path];
      delete newOriginalContents[path];
      newModifiedFiles.delete(path);

      return {
        ...prev,
        fileContents: newFileContents,
        originalFileContents: newOriginalContents,
        modifiedFiles: newModifiedFiles,
        fileTree: removeFromTree(prev.fileTree),
        activeFile: prev.activeFile?.path === path ? null : prev.activeFile
      };
    });
  };

  // Delete folder functionality
  const deleteFolder = async (path: string) => {
    const folderName = path.split('/').pop() || path;
    if (!window.confirm(`Are you sure you want to delete folder "${folderName}"?\n\nThis will delete all files inside this folder.\n\nThis action cannot be undone.`)) return;

    // Collect all files in this folder
    const collectFiles = (tree: FileTemplate[], folderPath: string): string[] => {
      const files: string[] = [];
      tree.forEach(item => {
        if (item.path.startsWith(folderPath + '/') || item.path === folderPath) {
          if (item.type === 'file') {
            files.push(item.path);
          }
          if (item.type === 'folder' && item.children) {
            files.push(...collectFiles(item.children, folderPath));
          }
        }
      });
      return files;
    };

    const filesToDelete = collectFiles(workspace.fileTree, path);

    // Delete from WebContainer
    try {
      const wc = await getWebContainer();
      for (const filePath of filesToDelete) {
        const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
        try {
          await wc.fs.rm(cleanPath);
        } catch (error) {
          console.error(`Failed to delete ${filePath} from WebContainer:`, error);
        }
      }
    } catch (error) {
      console.error('Failed to delete folder from WebContainer:', error);
    }

    // Remove from file tree
    const removeFromTree = (tree: FileTemplate[]): FileTemplate[] => {
      return tree
        .filter(item => item.path !== path && !item.path.startsWith(path + '/'))
        .map(item => {
          if (item.type === 'folder' && item.children) {
            return {
              ...item,
              children: removeFromTree(item.children)
            };
          }
          return item;
        });
    };

    setWorkspace(prev => {
      const newFileContents = { ...prev.fileContents };
      const newOriginalContents = { ...prev.originalFileContents };
      const newModifiedFiles = new Set(prev.modifiedFiles);

      // Remove all files in folder
      filesToDelete.forEach(filePath => {
        delete newFileContents[filePath];
        delete newOriginalContents[filePath];
        newModifiedFiles.delete(filePath);
      });

      return {
        ...prev,
        fileContents: newFileContents,
        originalFileContents: newOriginalContents,
        modifiedFiles: newModifiedFiles,
        fileTree: removeFromTree(prev.fileTree),
        activeFile: prev.activeFile && filesToDelete.includes(prev.activeFile.path) ? null : prev.activeFile
      };
    });
  };

  const copyToClipboard = (content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedCode(content);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const stopExecution = () => {
    try {
      executeProcRef.current?.kill();
    } catch { /* noop */ }
    executeProcRef.current = null;
    setIsExecuting(false);
    // The server (if any) is no longer running — clear its status.
    setServerUrl(null);
    setServerPort(null);
    setIsServerRunning(false);
  };

  // Add a custom API request to the tester (persisted with the project).
  const addApiRequest = () => {
    const name = newReq.name.trim();
    let url = newReq.url.trim();
    if (!name || !url) return;
    // Accept a bare path (e.g. "/users/1") — the in-container sender only uses
    // the path anyway, but we store an absolute URL for display/cURL clarity.
    if (url.startsWith('/')) url = `http://localhost:${serverPort ?? 3000}${url}`;
    const req: APIRequestTemplate = {
      id: `req-${Date.now()}`,
      name,
      method: newReq.method as APIRequestTemplate['method'],
      url,
      description: 'Custom request',
    };
    setWorkspace((prev) => ({
      ...prev,
      apiRequests: [...prev.apiRequests, req],
      activeApiRequest: req,
    }));
    setNewReq({ name: '', method: 'GET', url: '' });
    setShowAddReq(false);
  };

  const removeApiRequest = (id: string) => {
    setWorkspace((prev) => ({
      ...prev,
      apiRequests: prev.apiRequests.filter((r) => r.id !== id),
      activeApiRequest: prev.activeApiRequest?.id === id ? null : prev.activeApiRequest,
    }));
  };

  const renameApiRequest = (id: string, name: string) => {
    const trimmed = name.trim();
    setEditingReqId(null);
    if (!trimmed) return;
    setWorkspace((prev) => ({
      ...prev,
      apiRequests: prev.apiRequests.map((r) => (r.id === id ? { ...r, name: trimmed } : r)),
      activeApiRequest:
        prev.activeApiRequest?.id === id ? { ...prev.activeApiRequest, name: trimmed } : prev.activeApiRequest,
    }));
  };

  const methodPillClass = (m: string) =>
    m === 'GET' ? 'api-method api-method--get'
      : m === 'POST' ? 'api-method api-method--post'
        : m === 'PUT' ? 'api-method api-method--put'
          : m === 'PATCH' ? 'api-method api-method--patch'
            : m === 'DELETE' ? 'api-method api-method--delete'
              : 'api-method';

  // Run an HTTP request from INSIDE WebContainer against the running server on
  // localhost. This avoids the cross-origin (CORS) failure you get when the
  // browser fetches the preview URL directly — the request originates in the
  // container, same-origin with the server.
  const containerRequest = async (args: {
    method: string;
    path: string;
    headers: { [k: string]: string };
    body?: string;
  }) => {
    const wc = await getWebContainer();
    const url = `http://localhost:${serverPort ?? 3000}${args.path}`;
    const payload = JSON.stringify({ url, method: args.method, headers: args.headers, body: args.body });
    // Base64-encode the payload so no quotes/identifiers from the user's body
    // leak into the `node -e` source (WebContainer evals -e as TypeScript).
    const b64 = btoa(unescape(encodeURIComponent(payload)));
    const script =
      "(async()=>{const p=JSON.parse(Buffer.from('" + b64 + "','base64').toString('utf8'));" +
      'const t0=Date.now();' +
      'try{const res=await fetch(p.url,{method:p.method,headers:p.headers,body:p.body});' +
      'const text=await res.text();' +
      "process.stdout.write('__RESP__'+JSON.stringify({status:res.status,statusText:res.statusText,headers:Object.fromEntries(res.headers.entries()),body:text,time:Date.now()-t0}));" +
      "}catch(e){process.stdout.write('__RESP__'+JSON.stringify({__error:String((e&&e.message)||e)}));}})();";

    const proc = await wc.spawn('node', ['-e', script]);
    let out = '';
    const reader = proc.output.getReader();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      out += value;
    }
    await proc.exit;

    const marker = out.indexOf('__RESP__');
    if (marker === -1) {
      throw new Error(out.trim() || 'No response from the server (is it running?).');
    }
    const json = JSON.parse(out.slice(marker + '__RESP__'.length));
    if (json.__error) throw new Error(json.__error);

    let data: any = json.body;
    try { data = JSON.parse(json.body); } catch { /* keep as text */ }
    return {
      status: json.status,
      statusText: json.statusText,
      headers: json.headers || {},
      data,
      time: json.time || 0,
      size: (json.body || '').length,
    };
  };

  const executeCode = async () => {
    if (!workspace.activeFile) return;

    // Toggle behavior: if something is already running, the button is a Stop.
    if (isExecuting) {
      stopExecution();
      setExecutionOutput((prev) => `${prev}\n\n🛑 Stopped.`);
      return;
    }

    setIsExecuting(true);
    setShowTerminal(true);
    setBottomTab('output');
    setExecutionOutput('⏳ Starting server...\n');
    // Reset any stale server status from a previous run until this one is ready.
    setServerUrl(null);
    setServerPort(null);
    setIsServerRunning(false);

    // Defensive: kill any process we still have a handle to before starting a
    // new one, so re-running a server doesn't collide with its previous instance.
    if (executeProcRef.current) {
      try { executeProcRef.current.kill(); } catch { /* noop */ }
      executeProcRef.current = null;
      await new Promise((r) => setTimeout(r, 600)); // let the port release
    }

    // Kill any orphaned server still holding the port. In WebContainer we can't
    // use lsof/fuser, but we can spawn a small script that kills all node
    // processes except itself — effectively freeing any previously-bound port.
    try {
      const wc = await getWebContainer();
      const killScript = `
        try {
          const {execSync} = require('child_process');
          execSync('pkill -f "tsx src/app" 2>/dev/null || true');
          execSync('pkill -f "node src/app" 2>/dev/null || true');
        } catch(e) {}
      `.trim();
      const killProc = await wc.spawn('node', ['-e', killScript]);
      await Promise.race([killProc.exit, new Promise((r) => setTimeout(r, 2000))]);
      await new Promise((r) => setTimeout(r, 500));
    } catch { /* best effort — WebContainer may not support pkill */ }

    const language = getFileLanguage(workspace.activeFile.name);
    const cleanPath = workspace.activeFile.path.startsWith('/')
      ? workspace.activeFile.path.slice(1)
      : workspace.activeFile.path;

    // Execute in the in-browser WebContainer against the full mounted file tree.
    // This is the same sandboxed engine the terminal and `npm test` use, so
    // multi-file projects with relative imports resolve correctly — and no user
    // code ever runs on our servers.
    //
    // Note: a program may be long-lived (e.g. an Express server that listens
    // forever and never exits). We stream output live and keep the button as
    // "Stop" until the process exits or the user kills it — we do NOT block
    // waiting for an exit that may never come.
    try {
      const wc = await getWebContainer();

      // Auto-install dependencies on first run so the user doesn't have to.
      // `tsx` (and any deps) live in node_modules, which only exists after an
      // install — without this, Run fails with "command not found: tsx".
      let hasNodeModules = false;
      try {
        await wc.fs.readdir('node_modules');
        hasNodeModules = true;
      } catch { /* not installed yet */ }

      if (!hasNodeModules) {
        setExecutionOutput('⏳ Installing dependencies (first run, one-time)...\n');
        const install = await wc.spawn('npm', [
          'install',
          '--no-progress',
          '--no-audit',
          '--no-fund',
          '--loglevel=error',
        ]);
        executeProcRef.current = install; // allow Stop to cancel install
        let installOut = '';
        const ir = install.output.getReader();
        while (true) {
          const { done, value } = await ir.read();
          if (done) break;
          installOut += value;
          setExecutionOutput(`⏳ Installing dependencies...\n\n${installOut}`);
        }
        const installCode = await install.exit;
        if (executeProcRef.current !== install) return; // stopped mid-install
        executeProcRef.current = null;
        if (installCode !== 0) {
          setExecutionOutput((prev) => `${prev}\n\n❌ npm install failed (exit ${installCode}).`);
          setIsExecuting(false);
          return;
        }
      }

      let command: string;
      let args: string[];

      // Read package.json scripts to locate the project's entry file.
      let pkgScripts: Record<string, string> = {};
      try {
        const pkgRaw = await wc.fs.readFile('package.json', 'utf-8');
        const pkgText = typeof pkgRaw === 'string' ? pkgRaw : new TextDecoder().decode(pkgRaw);
        pkgScripts = JSON.parse(pkgText).scripts || {};
      } catch { /* no package.json or invalid — just run the active file */ }

      // Resolve the entry file: prefer the path referenced by the dev/start
      // script, otherwise fall back to the active file.
      const scriptForEntry = pkgScripts.dev || pkgScripts.start || '';
      const entryMatch = scriptForEntry.match(/(?:tsx|node)\s+(?:watch\s+)?([^\s'"&|]+\.[cm]?[jt]sx?)/);
      const entry = entryMatch ? entryMatch[1] : cleanPath;
      const entryIsTs = /\.[cm]?tsx?$/.test(entry) || language === 'typescript';

      // Run with auto-restart so code edits are picked up live (no manual
      // stop/run). Prefer an explicit `watch` script when a template ships one
      // (e.g. nodemon); otherwise use the runner's built-in watch — `tsx watch`
      // / `node --watch` — which needs no extra dependency.
      if (pkgScripts.watch) {
        command = 'npm';
        args = ['run', 'watch'];
      } else if (entryIsTs) {
        command = 'tsx';
        args = ['watch', entry];
      } else {
        command = 'node';
        args = ['--watch', entry];
      }

      const proc = await wc.spawn(command, args);
      executeProcRef.current = proc;

      const label = `${command} ${args.join(' ')}`;

      // Resolve the exit code in the background so we can clear the running
      // state when a short script ends, without blocking the live output
      // stream for long-lived servers.
      proc.exit.then((exitCode: number) => {
        if (executeProcRef.current !== proc) return; // superseded / stopped
        executeProcRef.current = null;
        setIsExecuting(false);
        // The process (and any server it hosted) has exited — clear server status.
        setServerUrl(null);
        setServerPort(null);
        setIsServerRunning(false);
        setExecutionOutput((prev) => {
          if (exitCode === 0) return `${prev}\n\n✅ Finished (exit code 0)`;
          let hint = '';
          if (/EADDRINUSE/i.test(prev)) {
            hint =
              '\n\n💡 That port is already in use — a previous server is still running. Click Stop, or reload the page to reset the sandbox, then Run again.';
          } else if (
            /command not found|not recognized|Cannot find (module|package)|ERR_MODULE_NOT_FOUND/i.test(prev)
          ) {
            hint =
              '\n\n💡 Tip: run `npm install` in the terminal first so dependencies and `tsx` are available.';
          }
          return `${prev}\n\n❌ Process exited with code ${exitCode}${hint}`;
        });
      }).catch(() => { /* killed */ });

      let output = '';
      const reader = proc.output.getReader();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        output += value;
        setExecutionOutput(`🚀 \`${label}\`\n\n${output}`);
      }
    } catch (error: any) {
      const message = error?.message || String(error);
      setExecutionOutput(
        `❌ Execution failed!\n\nError: ${message}\n\n💡 Tip: make sure the WebContainer has finished booting, then run \`npm install\` in the terminal before running multi-file projects.`,
      );
      console.error('❌ Code execution failed:', error);
      executeProcRef.current = null;
      setIsExecuting(false);
    }
  };

  // Runs the project's real test suite (`npm test`) inside WebContainer and
  // reports the genuine exit code / output. No simulated or random results.
  const runTests = async () => {
    setIsRunningTests(true);
    setShowTerminal(true);
    setBottomTab('output');
    setExecutionOutput('🧪 Running `npm test`...\n');
    setTestResults(null);

    try {
      const wc = await getWebContainer();
      const testProc = await wc.spawn('npm', ['test']);

      let output = '';
      const reader = testProc.output.getReader();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        output += value;
        setExecutionOutput(`🧪 Running \`npm test\`...\n\n${output}`);
      }

      const exitCode = await testProc.exit;
      const passed = exitCode === 0;

      setTestResults({
        passed,
        output,
        error: passed ? undefined : `Tests failed (exit code ${exitCode})`,
      });
      setExecutionOutput(
        `🧪 \`npm test\`\n\n${output}\n\n${passed ? '✅ All tests passed' : `❌ Tests failed (exit code ${exitCode})`}`,
      );

      // Persist the genuine result to the saved project, if any.
      if (dbProjectId) {
        machineCodingProjectService
          .update(dbProjectId, {
            testResults: {
              passed: passed ? 1 : 0,
              total: 1,
              lastRun: new Date().toISOString(),
            },
          })
          .catch((e) => console.error('Failed to persist test result:', e));
      }
    } catch (error: any) {
      const message = error?.message || String(error);
      setTestResults({
        passed: false,
        output: '',
        error: message,
      });
      setExecutionOutput(
        `❌ Could not run tests: ${message}\n\nTip: run \`npm install\` first, and make sure a "test" script exists in package.json.`,
      );
    } finally {
      setIsRunningTests(false);
    }
  };

  const getFileLanguage = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'ts': return 'typescript';
      case 'tsx': return 'typescript';
      case 'js': return 'javascript';
      case 'jsx': return 'javascript';
      case 'json': return 'json';
      case 'md': return 'markdown';
      case 'html': return 'html';
      case 'css': return 'css';
      case 'scss': return 'scss';
      default: return 'typescript';
    }
  };

  const renderCreateInput = () => (
    <div className="flex items-center px-2 py-1">
      {creatingNode?.type === 'folder' ? (
        <Folder className="w-4 h-4 ml-5 mr-2 text-blue-500 flex-shrink-0" />
      ) : (
        <File className="w-4 h-4 ml-5 mr-2 text-gray-400 flex-shrink-0" />
      )}
      <input
        autoFocus
        value={creatingName}
        onChange={(e) => setCreatingName(e.target.value)}
        onBlur={commitCreate}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitCreate();
          }
          if (e.key === 'Escape') {
            setCreatingNode(null);
            setCreatingName('');
          }
        }}
        placeholder={creatingNode?.type === 'folder' ? 'folder name' : 'file name.ext'}
        className="flex-1 min-w-0 px-1.5 py-0.5 text-sm bg-white dark:bg-gray-900 border border-primary-400 dark:border-primary-600 rounded outline-none text-gray-900 dark:text-white"
      />
    </div>
  );

  const renderFileTree = (files: FileTemplate[], depth: number = 0) => {
    return files.map((file) => (
      <div key={file.id} className="group">
        <div
          tabIndex={0}
          className={`flex items-center px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer rounded relative outline-none focus:ring-1 focus:ring-primary-400/50 ${(workspace.activeFile?.id === file.id || selectedPath === file.path) ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : ''
            }`}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedPath(file.path);
            setSelectedType(file.type);
            if (file.type === 'folder') {
              toggleFileFolder(file.id);
            } else {
              selectFile(file);
            }
          }}
          onKeyDown={(e) => {
            if (renamingPath === file.path) return;
            if (e.key === 'F2' || e.key === 'Enter') {
              e.preventDefault();
              beginRename(file);
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            setSelectedPath(file.path);
            setSelectedType(file.type);
            setContextMenu({
              x: e.clientX,
              y: e.clientY,
              folderPath: file.path,
              fileType: file.type,
              filePath: file.path
            });
          }}
        >
          <div className="flex items-center flex-1 min-w-0">
            {file.type === 'folder' ? (
              <>
                {file.isOpen ? (
                  <ChevronDown className="w-4 h-4 mr-1 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 mr-1 text-gray-500 flex-shrink-0" />
                )}
                {file.isOpen ? (
                  <FolderOpen className="w-4 h-4 mr-2 text-blue-500 flex-shrink-0" />
                ) : (
                  <Folder className="w-4 h-4 mr-2 text-blue-500 flex-shrink-0" />
                )}
              </>
            ) : (
              <div className="ml-5 mr-2 flex-shrink-0">
                {getFileIcon(file.name)}
              </div>
            )}
            {renamingPath === file.path ? (
              <input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitRename();
                  }
                  if (e.key === 'Escape') {
                    setRenamingPath(null);
                    setRenameValue('');
                  }
                }}
                className="flex-1 min-w-0 px-1 py-0.5 text-sm bg-white dark:bg-gray-900 border border-primary-400 dark:border-primary-600 rounded outline-none text-gray-900 dark:text-white"
              />
            ) : (
              <span className="text-sm text-gray-900 dark:text-white truncate flex-1">
                {file.name}
              </span>
            )}
          </div>
          {file.type === 'folder' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setContextMenu({
                  x: e.clientX,
                  y: e.clientY,
                  folderPath: file.path,
                  fileType: file.type,
                  filePath: file.path
                });
              }}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded flex-shrink-0 mr-1"
              title="More options"
            >
              <MoreVertical className="w-3 h-3 text-gray-500" />
            </button>
          )}
        </div>
        {file.type === 'folder' && file.isOpen && (
          <div className="ml-4">
            {file.children && renderFileTree(file.children, depth + 1)}
            {creatingNode && creatingNode.parentPath === file.path && renderCreateInput()}
          </div>
        )}
      </div>
    ));
  };

  if (templateLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading workspace…</span>
        </div>
      </div>
    );
  }

  if (templateNotFound || !template) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md px-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {loadError ? 'Could not load your project' : 'Template Not Found'}
          </h1>
          {loadError && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{loadError}</p>
          )}
          <Link
            to="/practice/machine-coding"
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg inline-flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Templates
          </Link>
        </div>
      </div>
    );
  }

  const handleOnboardingClose = () => {
    setShowOnboarding(false);
    setHasSeenOnboarding(true);
    const key = `onboarding_seen_${templateId}`;
    localStorage.setItem(key, 'true');
  };

  const handleOnboardingStart = () => {
    handleOnboardingClose();
  };

  return (
    <div className="machine-coding-workspace fixed inset-0 bg-gray-50 dark:bg-gray-900" style={{ top: (isFullscreen || bottomPanelMaximized) ? '0px' : '64px', zIndex: (isFullscreen || bottomPanelMaximized) ? 50 : undefined }}>
      {/* Onboarding Modal */}
      {showOnboarding && template && (
        <WorkspaceOnboarding
          template={template}
          onClose={handleOnboardingClose}
          onStart={handleOnboardingStart}
        />
      )}
      {/* CLEAN COMPACT HEADER */}
      <div className="h-12 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-4">
          <Link
            to="/practice/machine-coding"
            className="flex items-center text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            {projectName || template.name}
          </div>
          {dbProjectId && (
            <span
              className={`inline-flex items-center gap-1.5 text-xs ${saveStatus === 'error' ? 'text-rose-500' : 'text-gray-400'}`}
              title={saveStatus === 'error' ? 'Could not save — check your connection' : 'Your work autosaves to your account'}
            >
              {saveStatus === 'saving' ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
              ) : saveStatus === 'error' ? (
                <><X className="w-3 h-3" /> Save failed</>
              ) : (
                <><Check className="w-3 h-3 text-emerald-500" /> Saved</>
              )}
            </span>
          )}
          {!dbProjectId && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/60"
              title="This is an ephemeral session. Sign in to save your work to your account."
            >
              <Clock className="w-3 h-3" /> Not saved — sign in to keep your work
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowOnboarding(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="Open the project guide"
          >
            <Lightbulb className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Guide</span>
          </button>

          <button
            onClick={() => downloadProjectZip(template.name, workspace.fileContents)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="Download the project as a .zip (opens cleanly in VS Code)"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Download</span>
          </button>

          <button
            onClick={() => setShowTerminal(prev => !prev)}
            className={`machine-coding-terminal-toggle inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${showTerminal
              ? 'is-active'
              : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            title="Toggle bottom panel"
          >
            <TerminalIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Terminal</span>
          </button>

          <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />

          {/* Timer */}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            <span className="font-mono text-sm tabular-nums text-gray-700 dark:text-gray-200">
              {formatDuration(workspace.timerSeconds)}
            </span>
            {!workspace.timerRunning ? (
              <button onClick={startTimer} className="p-0.5 text-gray-400 hover:text-emerald-600" title="Start timer">
                <Play className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button onClick={pauseTimer} className="p-0.5 text-gray-400 hover:text-amber-600" title="Pause timer">
                <Pause className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={resetTimer} className="p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" title="Reset timer">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* MAIN LAYOUT */}
      {loadError && (
        <div className="px-4 py-2 text-sm bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border-b border-rose-200 dark:border-rose-800">
          {loadError}
        </div>
      )}
      <div className="flex" style={{ height: 'calc(100% - 48px)' }}>
        {/* LEFT EXPLORER (files & folders only) */}
        {showExplorer && (
          <div
            className="machine-coding-explorer bg-white dark:bg-gray-800 flex flex-col flex-shrink-0"
            style={{ width: `${explorerWidth}px` }}
          >
            <div className="h-9 flex items-center justify-between px-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">Files</span>
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => beginCreate(getCreateParent(), 'file')}
                  className="p-1 rounded-md text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-200/60 dark:hover:bg-gray-800"
                  title="New file (in selected folder)"
                >
                  <FilePlus className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => beginCreate(getCreateParent(), 'folder')}
                  className="p-1 rounded-md text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-200/60 dark:hover:bg-gray-800"
                  title="New folder (in selected folder)"
                >
                  <FolderPlus className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setShowExplorer(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-800"
                  title="Hide explorer"
                >
                  <PanelLeft className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {workspace.activePanel === 'files' && (
                <div>
                  <div className="space-y-1">
                    {renderFileTree(workspace.fileTree)}
                  </div>

                  {/* Context Menu */}
                  {contextMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setContextMenu(null)}
                      />
                      <div
                        className="fixed z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[180px]"
                        style={{
                          left: contextMenu.x,
                          top: contextMenu.y
                        }}
                      >
                        {contextMenu.fileType === 'folder' ? (
                          <>
                            <button
                              onClick={() => {
                                beginCreate(contextMenu.folderPath, 'file');
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                            >
                              <FilePlus className="w-4 h-4" />
                              <span>New File</span>
                            </button>
                            <button
                              onClick={() => {
                                beginCreate(contextMenu.folderPath, 'folder');
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                            >
                              <FolderPlus className="w-4 h-4" />
                              <span>New Folder</span>
                            </button>
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                            <button
                              onClick={() => {
                                const node = contextMenu.filePath
                                  ? findByPath(workspace.fileTree, contextMenu.filePath)
                                  : null;
                                if (node) beginRename(node);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                            >
                              <Pencil className="w-4 h-4" />
                              <span>Rename</span>
                            </button>
                            <button
                              onClick={() => {
                                if (contextMenu.filePath) {
                                  deleteFolder(contextMenu.filePath);
                                }
                                setContextMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete Folder</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                const node = contextMenu.filePath
                                  ? findByPath(workspace.fileTree, contextMenu.filePath)
                                  : null;
                                if (node) beginRename(node);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                            >
                              <Pencil className="w-4 h-4" />
                              <span>Rename</span>
                            </button>
                            <button
                              onClick={() => {
                                if (contextMenu.filePath) {
                                  deleteFile(contextMenu.filePath);
                                }
                                setContextMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete File</span>
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {workspace.activePanel === 'api' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">API Requests</h3>
                    {isServerRunning && serverUrl && (
                      <div className="px-2 py-1 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded text-xs">
                        <span className="text-green-700 dark:text-green-300">🟢 Server Running</span>
                      </div>
                    )}
                  </div>
                  {isServerRunning && serverUrl && (
                    <div className="mb-3 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-xs">
                      <p className="text-green-800 dark:text-green-300">
                        <strong>✅ Server Active:</strong> {serverUrl}
                      </p>
                      <p className="text-green-700 dark:text-green-400 mt-1">
                        APIs will use this URL automatically
                      </p>
                    </div>
                  )}
                  {!isServerRunning && (
                    <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs">
                      <p className="text-yellow-800 dark:text-yellow-300">
                        <strong>💡 Tip:</strong> Click "Run" to start the server, then test APIs against it here!
                      </p>
                    </div>
                  )}
                  <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {workspace.apiRequests.length === 0 ? (
                      <p className="text-sm text-gray-600 dark:text-gray-400">No API requests available</p>
                    ) : (
                      workspace.apiRequests.map((request) => (
                        <div
                          key={request.id}
                          onClick={() => setWorkspace(prev => ({ ...prev, activeApiRequest: request }))}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${workspace.activeApiRequest?.id === request.id
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm text-gray-900 dark:text-white">{request.name}</span>
                            <span className={`px-2 py-1 text-xs font-bold rounded ${methodPillClass(request.method)}`}>
                              {request.method}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{request.url}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {workspace.activePanel === 'packages' && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Package Manager</h3>
                  {(() => {
                    const packageJsonFile = workspace.fileTree.find(f => f.name === 'package.json');
                    const packageJsonContent = packageJsonFile ? workspace.fileContents[packageJsonFile.path] : null;
                    const packageJson = packageJsonContent ? JSON.parse(packageJsonContent) : { dependencies: {}, devDependencies: {} };

                    return (
                      <PackageManager
                        packageJson={packageJson}
                        onPackageJsonChange={(newPackageJson) => {
                          if (packageJsonFile) {
                            updateFileContent(packageJsonFile.path, JSON.stringify(newPackageJson, null, 2));
                          }
                        }}
                      />
                    );
                  })()}
                </div>
              )}

              {workspace.activePanel === 'tests' && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Run Tests</h3>
                  <div className="space-y-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Test Execution
                      </h4>
                      <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                        Run your tests using npm test or jest. Make sure you have a test framework installed.
                      </p>
                      <button
                        onClick={async () => {
                          setIsRunningTests(true);
                          try {
                            const wc = await getWebContainer();
                            const testProc = await wc.spawn('npm', ['test']);

                            let output = '';
                            const reader = testProc.output.getReader();
                            while (true) {
                              const { done, value } = await reader.read();
                              if (done) break;
                              const text = new TextDecoder().decode(value);
                              output += text;
                            }

                            const exitCode = await testProc.exit;
                            setTestResults({
                              passed: exitCode === 0,
                              output,
                              error: exitCode !== 0 ? 'Tests failed' : undefined
                            });
                          } catch (error: any) {
                            setTestResults({
                              passed: false,
                              output: '',
                              error: error.message
                            });
                          } finally {
                            setIsRunningTests(false);
                          }
                        }}
                        disabled={isRunningTests}
                        className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isRunningTests ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Running Tests...
                          </>
                        ) : (
                          <>
                            <PlayCircle className="w-4 h-4 mr-2" />
                            Run Tests (npm test)
                          </>
                        )}
                      </button>
                    </div>

                    {testResults && (
                      <div className={`p-4 rounded-lg border ${testResults.passed
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        }`}>
                        <h4 className={`font-semibold mb-2 ${testResults.passed
                          ? 'text-green-900 dark:text-green-100'
                          : 'text-red-900 dark:text-red-100'
                          }`}>
                          {testResults.passed ? '✅ Tests Passed' : '❌ Tests Failed'}
                        </h4>
                        {testResults.error && (
                          <p className="text-sm text-red-800 dark:text-red-200 mb-2">{testResults.error}</p>
                        )}
                        {testResults.output && (
                          <pre className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs max-h-64 overflow-y-auto whitespace-pre-wrap">
                            {testResults.output}
                          </pre>
                        )}
                      </div>
                    )}

                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Quick Commands</h4>
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center justify-between">
                          <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">npm test</code>
                          <span className="text-gray-600 dark:text-gray-400">Run all tests</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">npm run test:watch</code>
                          <span className="text-gray-600 dark:text-gray-400">Watch mode</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">jest</code>
                          <span className="text-gray-600 dark:text-gray-400">Run Jest directly</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {workspace.activePanel === 'timer' && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">Timer</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    Use the timer in the top toolbar to track your coding session. The timer runs independently of your code execution.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {showExplorer && (
          <div
            role="separator"
            aria-label="Resize file explorer"
            aria-orientation="vertical"
            aria-valuemin={220}
            aria-valuemax={480}
            aria-valuenow={explorerWidth}
            onMouseDown={(event) => {
              event.preventDefault();
              setIsExplorerResizing(true);
            }}
            onDoubleClick={() => setExplorerWidth(288)}
            className={`machine-coding-explorer-resizer flex-shrink-0 ${isExplorerResizing ? 'is-resizing' : ''}`}
            title="Drag to resize explorer · Double-click to reset"
          />
        )}

        {/* RIGHT COLUMN: editor (top) + terminal (bottom), VS Code style */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <div className="flex-1 flex flex-col min-h-0">
            {workspace.activeFile ? (
              <>
                <div className="h-12 flex items-center justify-between px-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {!showExplorer && (
                      <button
                        onClick={() => setShowExplorer(true)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-800"
                        title="Show explorer"
                      >
                        <PanelLeft className="w-4 h-4" />
                      </button>
                    )}
                    <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {workspace.activeFile.name}
                      {saveStatus === 'saving' && (
                        <span className="ml-1.5 text-amber-500" title="Saving…">●</span>
                      )}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {/* Server status */}
                    {serverUrl && (
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                        title="Your server is running inside the sandbox. Test it from the API tab."
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Port :{serverPort ?? 3000}
                      </span>
                    )}

                    {/* Reset (only when there are edits) */}
                    {workspace.modifiedFiles.size > 0 && (
                      <button
                        onClick={resetRepository}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md text-gray-600 dark:text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                        title="Reset all files to the original template"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Reset
                      </button>
                    )}

                    {template?.testCases && template.testCases.length > 0 && (
                      <button
                        onClick={runTests}
                        disabled={isRunningTests}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                        title="Run npm test"
                      >
                        <CheckCircle className="w-3 h-3" />
                        {isRunningTests ? 'Testing…' : 'Test'}
                      </button>
                    )}

                    {/* Run / Stop */}
                    <button
                      onClick={executeCode}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md text-white transition-colors ${isExecuting
                        ? serverUrl
                          ? 'bg-rose-600 hover:bg-rose-700'
                          : 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-primary-600 hover:bg-primary-700'
                        }`}
                      title={isExecuting ? (serverUrl ? 'Stop the running server' : 'Starting server... click to cancel') : 'Run the current file (Ctrl+Enter)'}
                    >
                      {isExecuting ? (
                        serverUrl ? (
                          <><X className="w-3 h-3" />Stop</>
                        ) : (
                          <><span className="w-3 h-3 rounded-full border-[1.5px] border-white/40 border-t-white animate-spin" />Starting...</>
                        )
                      ) : (
                        <><Play className="w-3 h-3" />Run</>
                      )}
                    </button>

                    {/* Copy */}
                    <button
                      onClick={() => copyToClipboard(workspace.fileContents[workspace.activeFile!.path] || '')}
                      className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-800"
                      title="Copy file contents"
                    >
                      {copiedCode === (workspace.fileContents[workspace.activeFile.path] || '') ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>

                    {/* Maximize editor */}
                    <button
                      onClick={() => setIsFullscreen((f) => !f)}
                      className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-800"
                      title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen workspace'}
                    >
                      {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* MONACO CODE EDITOR - Proper cursor sync and IntelliSense */}
                <div className="flex-1 min-h-0 relative">
                  <MonacoCodeEditor
                    value={workspace.fileContents[workspace.activeFile.path] || ''}
                    onChange={(value: string) => updateFileContent(workspace.activeFile!.path, value)}
                    filePath={workspace.activeFile.path}
                    language={getFileLanguage(workspace.activeFile.name)}
                    height="100%"
                    allFiles={workspace.fileContents}
                  />
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                  {workspace.fileTree.length === 0 ? (
                    <>
                      <div className="w-8 h-8 border-[3px] border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <h3 className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1">Setting up workspace</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Loading file system and dependencies...</p>
                    </>
                  ) : (
                    <>
                      <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                      <h3 className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1">No file selected</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Pick a file from the explorer to start editing.</p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* BOTTOM PANEL (VS Code-style dock: Terminal · Output · API · Packages) */}
          {showTerminal && (
            <div
              style={{ height: bottomPanelMaximized ? undefined : `${terminalHeight}px` }}
              className={`flex flex-col border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0 ${bottomPanelMaximized ? 'fixed inset-0 z-[60]' : ''}`}
            >
              {/* drag handle */}
              {!bottomPanelMaximized && (
                <div
                  onMouseDown={() => setIsDockResizing(true)}
                  className="h-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-primary-500 cursor-ns-resize flex-shrink-0"
                  title="Drag to resize"
                />
              )}
              {/* tab strip */}
              <div className="flex items-center h-9 px-2 gap-1 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 flex-shrink-0">
                {([
                  { id: 'terminal', label: 'Terminal' },
                  { id: 'output', label: 'Output' },
                  { id: 'api', label: 'API' },
                  { id: 'packages', label: 'Packages' },
                ] as const).map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setBottomTab(t.id)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${bottomTab === t.id
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                      }`}
                  >
                    {t.label}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-1">
                  <button
                    onClick={() => setBottomPanelMaximized((m) => !m)}
                    className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    title={bottomPanelMaximized ? 'Restore panel' : 'Maximize panel'}
                  >
                    {bottomPanelMaximized ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => { setShowTerminal(false); setBottomPanelMaximized(false); }}
                    className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    title="Close panel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* content */}
              <div className="flex-1 min-h-0 relative">
                {/* Terminal stays mounted to preserve the shell session */}
                <div className={bottomTab === 'terminal' ? 'absolute inset-0' : 'hidden'}>
                  <Terminal fill showHeader={false} isResizable={false} className="h-full" />
                </div>

                {bottomTab === 'output' && (
                  <div className="absolute inset-0 overflow-y-auto bg-[#1e1e1e] p-3">
                    {executionOutput ? (
                      <pre className="text-xs font-mono whitespace-pre-wrap text-gray-200 leading-relaxed">
                        {stripAnsi(executionOutput)}
                      </pre>
                    ) : (
                      <p className="text-xs text-gray-500 font-mono">Run your code to see output here.</p>
                    )}
                    {isExecuting && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-emerald-400">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> {serverUrl ? 'Server running' : 'Starting server...'} (Stop from the editor toolbar)
                      </div>
                    )}
                  </div>
                )}

                {bottomTab === 'api' && (
                  <div className="absolute inset-0 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-950">
                    <div className="mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">API</h3>
                      <p className="text-[11px] text-gray-400">Send requests to your running server and inspect responses.</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* Collection */}
                      <div className="lg:col-span-1 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 flex flex-col">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Requests</span>
                          <button
                            onClick={() => setShowAddReq((s) => !s)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            <Plus className="w-3.5 h-3.5" /> {showAddReq ? 'Cancel' : 'Add'}
                          </button>
                        </div>

                        {showAddReq && (
                          <div className="space-y-2 p-2.5 mb-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
                            <input
                              type="text"
                              placeholder="Name (e.g. Get user by id)"
                              value={newReq.name}
                              onChange={(e) => setNewReq({ ...newReq, name: e.target.value })}
                              className="w-full px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded focus:ring-2 focus:ring-primary-500/40 outline-none text-gray-900 dark:text-white placeholder-gray-400"
                            />
                            <div className="flex gap-2">
                              <select
                                value={newReq.method}
                                onChange={(e) => setNewReq({ ...newReq, method: e.target.value })}
                                className="px-2 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded outline-none text-gray-900 dark:text-white"
                              >
                                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                              <input
                                type="text"
                                placeholder="/users/USER001"
                                value={newReq.url}
                                onChange={(e) => setNewReq({ ...newReq, url: e.target.value })}
                                className="flex-1 px-2 py-1.5 text-sm font-mono bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded outline-none text-gray-900 dark:text-white placeholder-gray-400"
                              />
                            </div>
                            <button
                              onClick={addApiRequest}
                              disabled={!newReq.name.trim() || !newReq.url.trim()}
                              className="w-full px-3 py-1.5 text-sm font-semibold rounded bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Add request
                            </button>
                          </div>
                        )}

                        {workspace.apiRequests.filter((r) => r && r.name && r.url).length === 0 && !showAddReq && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 py-2">
                            No requests yet. Click <strong>Add</strong> to test a route you wrote (e.g. <span className="font-mono">GET /users/:id</span>).
                          </p>
                        )}

                        <div className="space-y-1.5 overflow-y-auto">
                          {workspace.apiRequests.filter((r) => r && r.name && r.url).map((req) => (
                            <div
                              key={req.id}
                              onClick={() => setWorkspace((prev) => ({ ...prev, activeApiRequest: req }))}
                              className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-pointer transition-colors ${workspace.activeApiRequest?.id === req.id ? 'border-primary-300 dark:border-primary-700 bg-primary-50/70 dark:bg-primary-900/20' : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                            >
                              <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${methodPillClass(req.method)}`}>
                                {req.method}
                              </span>
                              <div className="flex-1 min-w-0">
                                {editingReqId === req.id ? (
                                  <input
                                    autoFocus
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    onBlur={() => renameApiRequest(req.id, editingName)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') renameApiRequest(req.id, editingName);
                                      if (e.key === 'Escape') setEditingReqId(null);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full px-1.5 py-0.5 text-sm bg-white dark:bg-gray-900 border border-primary-300 dark:border-primary-700 rounded outline-none text-gray-900 dark:text-white"
                                  />
                                ) : (
                                  <>
                                    <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{req.name}</span>
                                    <span className="block text-[11px] text-gray-400 truncate font-mono">{req.url}</span>
                                  </>
                                )}
                              </div>
                              {editingReqId !== req.id && (
                                <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setEditingReqId(req.id); setEditingName(req.name); }}
                                    className="p-1 text-gray-300 hover:text-primary-500"
                                    title="Rename"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); removeApiRequest(req.id); }}
                                    className="p-1 text-gray-300 hover:text-rose-500"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Request */}
                      <div className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
                        {workspace.activeApiRequest ? (
                          <ApiTester
                            request={workspace.activeApiRequest}
                            baseUrl={serverUrl}
                            liveOrigin={serverPort ? `http://localhost:${serverPort}` : null}
                            containerRequest={containerRequest}
                            onChange={(patch) => setWorkspace((prev) => ({
                              ...prev,
                              apiRequests: prev.apiRequests.map((r) => (r.id === patch.id ? ({ ...r, ...patch } as APIRequestTemplate) : r)),
                              activeApiRequest: prev.activeApiRequest?.id === patch.id
                                ? ({ ...prev.activeApiRequest, ...patch } as APIRequestTemplate)
                                : prev.activeApiRequest,
                            }))}
                            onResponse={(r) => setWorkspace(prev => ({ ...prev, apiResponse: r }))}
                          />
                        ) : (
                          <div className="h-full flex items-center justify-center text-center py-10">
                            <div>
                              <Zap className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                              <p className="text-sm text-gray-600 dark:text-gray-400">Select a request on the left, or add one to start testing.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {bottomTab === 'packages' && (
                  <div className="absolute inset-0 overflow-y-auto p-4 bg-white dark:bg-gray-900">
                    {(() => {
                      // package.json may be nested inside a root folder — search recursively.
                      const findPkgFile = (files: FileTemplate[]): FileTemplate | null => {
                        for (const f of files) {
                          if (f.type === 'file' && f.name === 'package.json') return f;
                          if (f.children) {
                            const found = findPkgFile(f.children);
                            if (found) return found;
                          }
                        }
                        return null;
                      };
                      const pkgFile = findPkgFile(workspace.fileTree);
                      const pkgContent = pkgFile ? workspace.fileContents[pkgFile.path] : null;
                      let pkg: any = { dependencies: {}, devDependencies: {} };
                      try { if (pkgContent) pkg = JSON.parse(pkgContent); } catch { /* ignore */ }
                      return (
                        <PackageManager
                          packageJson={pkg}
                          onPackageJsonChange={(next) => { if (pkgFile) updateFileContent(pkgFile.path, JSON.stringify(next, null, 2)); }}
                        />
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

export default MachineCodingWorkspace;
